import { useState } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { api, qs } from "@/lib/api";
import { formatAgo, formatDateTime, formatInt } from "@/lib/format";
import { BrandMark } from "@/components/BrandMark";
import { Empty, PageHeader } from "@/components/PageHeader";
import { useToast } from "@/components/Toaster";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FormSettingsDialog, type FormSettings } from "./FormSettingsDialog";
import { plainSignal, signalsOf } from "./spamSignals";
import { LoadError } from "@/components/LoadError";

interface Form {
  id: number;
  brand_id: number;
  key: string;
  name: string;
  description: string | null;
  active: boolean;
  settings: FormSettings;
  submissions_30d: number;
  spam_30d: number;
  failed_30d: number;
  last_submission_at: string | null;
}
interface Submission {
  id: number;
  form_key: string;
  brand_id: number | null;
  contact_id: number | null;
  email: string | null;
  name: string;
  status: "received" | "processed" | "spam" | "failed";
  received_at: string;
  processed_at: string | null;
  error: string | null;
  result: Record<string, unknown> | null;
}
interface SubmissionDetail extends Submission {
  fields: Record<string, unknown>;
  attribution: Record<string, unknown>;
  client_checks: Record<string, unknown>;
  ip: string | null;
}

/** Website forms: how each is doing, and the queues a person looks at (spam held back, failures). */
export function FormsPage() {
  const forms = useQuery({ queryKey: ["forms"], queryFn: () => api<{ forms: Form[]; unknown_keys: string[] }>("/api/v1/forms") });
  const spamCount = (forms.data?.forms ?? []).reduce((a, f) => a + f.spam_30d, 0);
  const failedCount = (forms.data?.forms ?? []).reduce((a, f) => a + f.failed_30d, 0);
  return (
    <div className="mx-auto max-w-[1200px]">
      <PageHeader title="Website forms" sub="sign-ups, contact forms and loan requests from the five sites">
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Spam is held back here, not deleted. Better that some spam gets through than a real customer is blocked, so look over the spam list now and then and let
          anyone real through.
        </p>
      </PageHeader>
      <Tabs defaultValue="spam">
        <TabsList>
          <TabsTrigger value="spam">Held as spam{spamCount ? ` (${formatInt(spamCount)} in 30 days)` : ""}</TabsTrigger>
          <TabsTrigger value="failed">Failed{failedCount ? ` (${formatInt(failedCount)})` : ""}</TabsTrigger>
          <TabsTrigger value="recent">Everything recent</TabsTrigger>
          <TabsTrigger value="forms">Forms</TabsTrigger>
        </TabsList>
        <TabsContent value="spam" className="pt-3">
          <Queue status="spam" />
        </TabsContent>
        <TabsContent value="failed" className="pt-3">
          <Queue status="failed" />
        </TabsContent>
        <TabsContent value="recent" className="pt-3">
          <Queue />
        </TabsContent>
        <TabsContent value="forms" className="pt-3">
          <FormsTable forms={forms.data?.forms ?? []} unknown={forms.data?.unknown_keys ?? []} loading={forms.isLoading} error={forms.error} onRetry={() => forms.refetch()} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function FormsTable({ forms, unknown, loading, error, onRetry }: { forms: Form[]; unknown: string[]; loading: boolean; error?: Error | null; onRetry?: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState<Form | null>(null);
  const install = useMutation({
    mutationFn: () => api<{ created: string[]; kept: string[] }>("/api/v1/forms/install-defaults", { json: {} }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["forms"] });
      toast(r.created.length ? `Set up ${r.created.length} form${r.created.length === 1 ? "" : "s"}: ${r.created.join(", ")}` : "All the standard forms were already set up");
    },
    onError: (e) => toast(e.message, "error"),
  });
  return (
    <Card className="overflow-x-auto">
      <div className="flex flex-wrap items-center gap-2 border-b px-4 py-2 text-sm">
        <span className="text-[var(--color-muted-foreground)]">Each form decides which tags, notes, automations and alerts a submission gets.</span>
        <Button
          size="sm"
          variant="outline"
          className="ml-auto"
          disabled={install.isPending}
          onClick={() => window.confirm("Set up the standard forms for the five sites (newsletter sign-ups, contact forms, MHU loan request…)? Forms that already exist are left as they are.") && install.mutate()}
        >
          Set up the standard site forms
        </Button>
      </div>
      {loading && <Skeleton className="m-3 h-32" />}
      {error && <LoadError what="forms" error={error} onRetry={onRetry} />}
      {!loading && !error && forms.length === 0 && <Empty title="No forms set up yet." />}
      {forms.length > 0 && (
        <Table>
          <THead>
            <tr>
              <TH>Form</TH>
              <TH>Brand</TH>
              <TH>On</TH>
              <TH className="text-right">Last 30 days</TH>
              <TH className="text-right">Spam</TH>
              <TH className="text-right">Failed</TH>
              <TH>Last one</TH>
              <TH />
            </tr>
          </THead>
          <tbody>
            {forms.map((f) => (
              <TR key={f.id} className={f.active ? "" : "row-dim"}>
                <TD className="max-w-[22rem]">
                  <div className="truncate font-medium">{f.name}</div>
                  <div className="num truncate text-xs text-[var(--color-muted-foreground)]">{f.key}</div>
                </TD>
                <TD>
                  <BrandMark id={f.brand_id} />
                </TD>
                <TD>{f.active ? <Badge variant="success">on</Badge> : <Badge variant="muted">off</Badge>}</TD>
                <TD className="num text-right">{formatInt(f.submissions_30d)}</TD>
                <TD className="num text-right">{f.spam_30d ? formatInt(f.spam_30d) : ""}</TD>
                <TD className="num text-right">{f.failed_30d ? formatInt(f.failed_30d) : ""}</TD>
                <TD className="whitespace-nowrap text-xs">{f.last_submission_at ? formatAgo(f.last_submission_at) : "never"}</TD>
                <TD className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(f)}>
                    Edit
                  </Button>
                </TD>
              </TR>
            ))}
          </tbody>
        </Table>
      )}
      {unknown.length > 0 && (
        <p className="border-t px-4 py-2 text-xs text-[var(--color-muted-foreground)]">
          Sites are posting form keys that aren't set up yet (stored, with no extra tags or automations): {unknown.join(", ")}.
        </p>
      )}
      {editing && <FormSettingsDialog form={editing} onClose={() => setEditing(null)} />}
    </Card>
  );
}

const STATUS: Record<Submission["status"], { label: string; variant: "success" | "warning" | "destructive" | "muted" | "default" }> = {
  received: { label: "waiting", variant: "default" },
  processed: { label: "done", variant: "success" },
  spam: { label: "held as spam", variant: "warning" },
  failed: { label: "failed", variant: "destructive" },
};

function Queue({ status }: { status?: Submission["status"] }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState<number | null>(null);
  const q = useInfiniteQuery({
    queryKey: ["form-submissions", status ?? "all"],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => api<{ submissions: Submission[]; next_before_id?: number }>("/api/v1/form-submissions" + qs({ status, before_id: pageParam || undefined, limit: 50 })),
    getNextPageParam: (l) => l.next_before_id || undefined,
  });
  const reprocess = useMutation({
    mutationFn: ({ id, notSpam }: { id: number; notSpam: boolean }) => api(`/api/v1/form-submissions/${id}/reprocess`, { json: { not_spam: notSpam } }),
    onSuccess: (_r, v) => {
      void qc.invalidateQueries({ queryKey: ["form-submissions"] });
      void qc.invalidateQueries({ queryKey: ["forms"] });
      toast(v.notSpam ? "Let through: it's being processed like any real submission" : "Running it again");
      setOpen(null);
    },
    onError: (e) => toast(e.message, "error"),
  });
  const markSpam = useMutation({
    mutationFn: (id: number) => api(`/api/v1/form-submissions/${id}/spam`, { method: "POST" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["form-submissions"] });
      toast("Marked as spam");
      setOpen(null);
    },
    onError: (e) => toast(e.message, "error"),
  });
  const rows = q.data?.pages.flatMap((p) => p.submissions) ?? [];
  return (
    <Card className="overflow-x-auto">
      {q.isLoading && <Skeleton className="m-3 h-32" />}
      {q.error && <LoadError what="submissions" error={q.error} onRetry={() => (q.isFetchNextPageError ? q.fetchNextPage() : q.refetch())} />}
      {!q.isLoading && !q.error && rows.length === 0 && <Empty title={status === "spam" ? "Nothing held as spam." : status === "failed" ? "No failed submissions." : "No submissions yet."} />}
      {rows.length > 0 && (
        <Table>
          <THead>
            <tr>
              <TH>Received (MT)</TH>
              <TH>Who</TH>
              <TH>Form</TH>
              <TH>Status</TH>
              <TH />
            </tr>
          </THead>
          <tbody>
            {rows.map((s) => (
              <TR key={s.id}>
                <TD className="num whitespace-nowrap">{formatDateTime(s.received_at)}</TD>
                <TD className="max-w-[18rem]">
                  {s.contact_id ? (
                    <Link to="/contacts/$contactId" params={{ contactId: String(s.contact_id) }} className="block truncate text-[var(--color-primary)] hover:underline">
                      {s.name || s.email || `#${s.contact_id}`}
                    </Link>
                  ) : (
                    <span className="block truncate">{s.name || "(no name)"}</span>
                  )}
                  <span className="block truncate text-xs text-[var(--color-muted-foreground)]">{s.email}</span>
                </TD>
                <TD className="max-w-[14rem]">
                  <span className="flex items-center gap-1.5">
                    {s.brand_id ? <BrandMark id={s.brand_id} /> : null}
                    <span className="num truncate text-xs">{s.form_key}</span>
                  </span>
                </TD>
                <TD>
                  <Badge variant={STATUS[s.status].variant}>{STATUS[s.status].label}</Badge>
                  {s.status === "spam" && signalsOf(s.result).length > 0 && (
                    <div className="max-w-[18rem] text-xs text-[var(--color-muted-foreground)]">{signalsOf(s.result).map(plainSignal).join("; ")}</div>
                  )}
                  {s.error && <div className="max-w-[16rem] truncate text-xs text-[var(--color-muted-foreground)]" title={s.error}>{s.error}</div>}
                </TD>
                <TD className="whitespace-nowrap text-right">
                  <Button size="sm" variant="ghost" onClick={() => setOpen(s.id)}>
                    Look
                  </Button>
                  {s.status === "spam" && (
                    <Button size="sm" variant="outline" disabled={reprocess.isPending} onClick={() => reprocess.mutate({ id: s.id, notSpam: true })}>
                      Not spam: let through
                    </Button>
                  )}
                  {s.status === "failed" && (
                    <Button size="sm" variant="outline" disabled={reprocess.isPending} onClick={() => reprocess.mutate({ id: s.id, notSpam: false })}>
                      Try again
                    </Button>
                  )}
                </TD>
              </TR>
            ))}
          </tbody>
        </Table>
      )}
      {q.hasNextPage && (
        <div className="border-t p-2">
          <Button size="sm" variant="ghost" onClick={() => q.fetchNextPage()} disabled={q.isFetchingNextPage}>
            Show more
          </Button>
        </div>
      )}
      {open && <SubmissionSheet id={open} onClose={() => setOpen(null)} onLetThrough={(id) => reprocess.mutate({ id, notSpam: true })} onSpam={(id) => markSpam.mutate(id)} />}
    </Card>
  );
}

function SubmissionSheet({ id, onClose, onLetThrough, onSpam }: { id: number; onClose: () => void; onLetThrough: (id: number) => void; onSpam: (id: number) => void }) {
  const q = useQuery({ queryKey: ["form-submission", id], queryFn: () => api<SubmissionDetail>(`/api/v1/form-submissions/${id}`) });
  const s = q.data;
  const kv = (o: Record<string, unknown> | null | undefined) =>
    Object.entries(o ?? {}).filter(([, v]) => v !== null && v !== "" && !(typeof v === "object" && v !== null && Object.keys(v).length === 0));
  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent title={`Form submission #${id}`} description="Everything the visitor sent and what the CRM did with it. Viewing it is recorded in the audit log.">
        {q.isLoading && <Skeleton className="m-4 h-40" />}
        {q.error && <LoadError what="this submission" error={q.error} onRetry={() => q.refetch()} />}
        {s && (
          <div className="space-y-4 p-4 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={STATUS[s.status].variant}>{STATUS[s.status].label}</Badge>
              <span className="num text-xs text-[var(--color-muted-foreground)]">
                {s.form_key}, received {formatDateTime(s.received_at)}
                {s.ip ? ` from ${s.ip}` : ""}
              </span>
            </div>
            {s.error && <p className="rounded-md bg-[var(--color-danger-bg)] px-3 py-2 text-xs text-[var(--color-danger-fg)]">{s.error}</p>}
            {signalsOf(s.result).length > 0 && (
              <section>
                <h4 className="mb-1 text-xs font-semibold text-[var(--color-muted-foreground)]">Why it looked like spam</h4>
                <ul className="list-inside list-disc text-sm">
                  {signalsOf(s.result).map((g) => (
                    <li key={g}>{plainSignal(g)}</li>
                  ))}
                </ul>
              </section>
            )}
            <Section title="What they typed" rows={kv(s.fields)} />
            <Section title="Where they came from" rows={kv(s.attribution)} />
            <Section title="The site's checks" rows={kv(s.client_checks)} />
            <Section title="What the CRM did" rows={kv(s.result)} />
            <div className="flex flex-wrap gap-2 border-t pt-3">
              {s.status === "spam" && <Button onClick={() => onLetThrough(s.id)}>Not spam: let through</Button>}
              {(s.status === "received" || s.status === "failed") && (
                <Button variant="outline" onClick={() => onSpam(s.id)}>
                  Mark as spam
                </Button>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Section({ title, rows }: { title: string; rows: [string, unknown][] }) {
  if (rows.length === 0) return null;
  return (
    <section>
      <h4 className="mb-1 text-xs font-semibold text-[var(--color-muted-foreground)]">{title}</h4>
      <dl className="grid grid-cols-[10rem_minmax(0,1fr)] gap-x-3 gap-y-1">
        {rows.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="truncate text-xs text-[var(--color-muted-foreground)]">{k.replace(/_/g, " ")}</dt>
            <dd className="break-words">{typeof v === "object" ? JSON.stringify(v) : String(v)}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

